import React, { useState } from "react";
import { Send, FileText, MapPin, Upload, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { uploadFile, submitQuote } from "../lib/api";

const inputCls =
  "rounded-none h-12 bg-background border-border focus-visible:border-primary focus-visible:ring-primary";
const triggerCls =
  "rounded-none h-12 bg-background border-border focus:border-primary focus:ring-primary";

export const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", projectType: "", projectSize: "",
    location: "", timeline: "", description: "", budget: "",
  });
  const [photos, setPhotos] = useState([]); // {url, filename, preview}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (files.length + photos.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 10MB`);
          continue;
        }
        const res = await uploadFile(file);
        setPhotos((prev) => [
          ...prev,
          { url: res.url, filename: res.filename, preview: URL.createObjectURL(file) },
        ]);
      }
    } catch (err) {
      toast.error("Photo upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) =>
    setPhotos((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.projectType || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote({
        ...formData,
        photos: photos.map((p) => ({ url: p.url, filename: p.filename })),
      });
      toast.success("Quote request submitted! Someone will reach out for more information.");
      setFormData({
        name: "", email: "", phone: "", projectType: "", projectSize: "",
        location: "", timeline: "", description: "", budget: "",
      });
      setPhotos([]);
    } catch (err) {
      toast.error("Submission failed. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 hazard-stripe opacity-[0.04]" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="label-kicker mb-4">Free Estimate</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none">
            Get Your <span className="text-primary">Quote</span>
          </h2>
          <p className="mt-5 text-gray-400 text-lg max-w-2xl mx-auto">
            Tell us about your project. Fill out the form and someone will reach
            out for more information.
          </p>
        </div>

        <div className="card-industrial p-8 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
            {/* Contact info */}
            <div>
              <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white mb-5 flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" /> Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="label-kicker mb-2 block">Full Name *</Label>
                  <Input data-testid="quote-name-input" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="John Smith" className={inputCls} required />
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Email *</Label>
                  <Input data-testid="quote-email-input" type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="you@email.com" className={inputCls} required />
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Phone</Label>
                  <Input data-testid="quote-phone-input" type="tel" value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} placeholder="(703) 000-0000" className={inputCls} />
                </div>
                <div>
                  <Label className="label-kicker mb-2 block"><MapPin className="h-3.5 w-3.5 inline mr-1" />Project Location</Label>
                  <Input data-testid="quote-location-input" value={formData.location} onChange={(e) => handleChange("location", e.target.value)} placeholder="City, State" className={inputCls} />
                </div>
              </div>
            </div>

            {/* Project details */}
            <div>
              <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white mb-5">Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="label-kicker mb-2 block">Project Type *</Label>
                  <Select value={formData.projectType} onValueChange={(v) => handleChange("projectType", v)}>
                    <SelectTrigger data-testid="quote-type-select" className={triggerCls}><SelectValue placeholder="Select project type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential Demolition</SelectItem>
                      <SelectItem value="commercial">Commercial Demolition</SelectItem>
                      <SelectItem value="site-clearing">Site Clearing</SelectItem>
                      <SelectItem value="project-management">Project Management &amp; Oversight</SelectItem>
                      <SelectItem value="interior">Interior Demolition</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Project Size</Label>
                  <Select value={formData.projectSize} onValueChange={(v) => handleChange("projectSize", v)}>
                    <SelectTrigger data-testid="quote-size-select" className={triggerCls}><SelectValue placeholder="Select project size" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small (&lt; 1,000 sq ft)</SelectItem>
                      <SelectItem value="medium">Medium (1,000 - 5,000 sq ft)</SelectItem>
                      <SelectItem value="large">Large (5,000 - 15,000 sq ft)</SelectItem>
                      <SelectItem value="extra-large">Extra Large (&gt; 15,000 sq ft)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Desired Timeline</Label>
                  <Select value={formData.timeline} onValueChange={(v) => handleChange("timeline", v)}>
                    <SelectTrigger data-testid="quote-timeline-select" className={triggerCls}><SelectValue placeholder="Select timeline" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">ASAP (Within 2 weeks)</SelectItem>
                      <SelectItem value="1month">Within 1 month</SelectItem>
                      <SelectItem value="3months">Within 3 months</SelectItem>
                      <SelectItem value="6months">Within 6 months</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Budget Range</Label>
                  <Select value={formData.budget} onValueChange={(v) => handleChange("budget", v)}>
                    <SelectTrigger data-testid="quote-budget-select" className={triggerCls}><SelectValue placeholder="Select budget range" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-5k">Under $5,000</SelectItem>
                      <SelectItem value="5k-15k">$5,000 - $15,000</SelectItem>
                      <SelectItem value="15k-50k">$15,000 - $50,000</SelectItem>
                      <SelectItem value="50k-plus">$50,000+</SelectItem>
                      <SelectItem value="discuss">Prefer to discuss</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="label-kicker mb-2 block">Project Description *</Label>
              <Textarea data-testid="quote-description-input" value={formData.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="Provide details about your project, requirements, or questions." rows={5} className="rounded-none bg-background border-border focus-visible:border-primary focus-visible:ring-primary" required />
            </div>

            {/* Photo upload */}
            <div>
              <Label className="label-kicker mb-2 block">Project Photos (Optional · Max 5)</Label>
              <label
                htmlFor="quote-photo-upload"
                data-testid="quote-photo-dropzone"
                className="flex flex-col items-center justify-center border border-dashed border-border bg-background py-8 cursor-pointer hover:border-primary transition-colors"
              >
                <input id="quote-photo-upload" type="file" multiple accept="image/*" onChange={handleFileUpload} className="hidden" />
                {uploading ? (
                  <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
                ) : (
                  <Upload className="h-8 w-8 text-primary mb-2" />
                )}
                <p className="text-sm text-gray-300">
                  {uploading ? "Uploading..." : "Click to upload photos"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB each</p>
              </label>

              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="quote-photo-previews">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img src={photo.preview} alt={photo.filename} className="w-full h-20 object-cover border border-border" />
                      <button type="button" onClick={() => removePhoto(index)} data-testid={`quote-remove-photo-${index}`} className="absolute -top-2 -right-2 bg-destructive text-white w-5 h-5 flex items-center justify-center hover:bg-red-700">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-center pt-2">
              <Button data-testid="quote-submit-btn" type="submit" size="lg" disabled={submitting || uploading} className="rounded-none font-bold uppercase tracking-widest px-12 py-6">
                {submitting ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>
                ) : (
                  <><Send className="mr-2 h-5 w-5" /> Submit Quote Request</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
